import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { forkJoin } from 'rxjs';
import { ElementType } from 'src/app/_objects/element-type';
import { ReportCustomElement } from 'src/app/_objects/report-custom-element';
import { TemplateCustomPageSection } from 'src/app/_objects/report-template';
import { CommonSnackBarService } from 'src/app/_services/common-snack-bar.service';
import { LangUpdateService } from 'src/app/_services/lang-update.service';
import { ReportComponentService } from 'src/app/_services/report-component.service';
import { MousePoint, Notifier, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';

@Component({
    selector: 'app-report-custom-element',
    templateUrl: './report-custom-element.component.html',
    styleUrls: ['./report-custom-element.component.scss'],
    standalone: false
})
export class ReportCustomElementComponent implements OnInit {
  @Input() public pageKey: string;
  @Input() public customElementsKey: string;
  @Input() public elementLocation: string;
  @Input() public isNewApp: any;

  public notifier: Notifier;
  public reportCustomElemet: ReportCustomElement;
  public resizeMarkerSize: number;
  public resizing: boolean;
  public applications: string[] = [this.translate.instant('customElement.ViSOL'), this.translate.instant('customElement.IPM'), this.translate.instant('customElement.Generic')];
  public application: string;
  public customElementName: string;
  private elements = ['texts', 'tables', 'sections', 'rectangles', 'lines', 'images', 'dropDownLists'];
  constructor(public designService: ReportTemplateDesignService,
    private reportComponentService: ReportComponentService,
    private snackBar: CommonSnackBarService,
    private spinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService) {
    this.langService.prepare
      (this, this.translate, (res) => {
        this.applications[0] = this.translate.instant('customElement.ViSOL');
        this.applications[1] = this.translate.instant('customElement.IPM');
        this.applications[2] = this.translate.instant('customElement.Generic');
      }
      );
    this.resizeMarkerSize = 36;
    this.resizing = false;
  }

  ngOnInit(): void {
    this.notifier = {
      componentName: 'report-custom-element.component',
      ids: { pageId: this.pageKey, elementId: this.customElementsKey }
    };
    this.reportCustomElemet = this.designService.getCustomElements(this.customElementsKey);
    const elementNavigator = this.designService.navigatorElements
      .filter((x) => x.pageKey === this.pageKey)[0]
      .elementsDetails.filter((y) => y.location === this.elementLocation)[0]
      .elements;

    elementNavigator.filter(x => x.elementName === ElementType.CustomElement).length > 0
      ? elementNavigator[0].elementData.push({
        id: this.customElementsKey,
        page: this.pageKey,
        name: this.reportCustomElemet.titleText,
      })
      : elementNavigator.push({
        elementName: ElementType.CustomElement,
        isVisible: false,
        elementData: [
          {
            id: this.customElementsKey,
            page: this.pageKey,
            name: this.reportCustomElemet.titleText,
          },
        ],
      });
    this.application = this.designService.currentCEApplication;
    this.customElementName = this.designService.currentCEName;
    if (this.designService.isClickedOnCEUpdate) {
      this.designService.customElementAdd = true;
    }
  }

  public onClick(event: MouseEvent) {
    if (this.reportCustomElemet.isSelectable) {
      this.designService.notifySelectElement(this.notifier, this.customElementsKey, event.ctrlKey);
      event.stopPropagation();
    }
  }

  public getStyles(reportCustomElemet: ReportCustomElement): object {
    return {
      width: reportCustomElemet.width,
      height: reportCustomElemet.itemContainerHeight,
      fontSize: reportCustomElemet.fontSize,
      fontFamily: reportCustomElemet.fontFamily,
      border: `${reportCustomElemet.borderWidth} ${reportCustomElemet.borderStyle} ${reportCustomElemet.borderColor}`,
      padding: `${reportCustomElemet.itemMargin}mm`
    };
  }

  public getContainerStyle(reportCustomElemet: ReportCustomElement): object {
    return {
      top: reportCustomElemet.top,
      left: reportCustomElemet.left,
      width: reportCustomElemet.width,
      height: reportCustomElemet.height
    };
  }

  public getItemStyles(reportCustomElemet: ReportCustomElement): object {
    return {
      width: reportCustomElemet.width,
      height: reportCustomElemet.height,
      border: `${reportCustomElemet.borderWidth} ${reportCustomElemet.borderStyle} ${reportCustomElemet.borderColor}`,
      margin: reportCustomElemet.itemMargin
    };
  }

  onsave() {
    this.spinnerService.show(this.spinnerService.mainSpinnerId);
    const template = new TemplateCustomPageSection();
    const pageIndex = this.designService.getPageIds().findIndex(x => x == this.pageKey);
    if (this.designService.isClickedOnCEUpdate) {
      const editTemlate = this.getElementsOnUpdate();
      template.texts = [...this.designService.customElementResponse.texts, ...editTemlate.texts];
      template.tables = [...this.designService.customElementResponse.tables, ...editTemlate.tables];
      template.sections = [...this.designService.customElementResponse.sections, ...editTemlate.sections];
      template.rectangles = [...this.designService.customElementResponse.rectangles, ...editTemlate.rectangles];
      template.lines = [...this.designService.customElementResponse.lines, ...editTemlate.lines];
      template.images = [...this.designService.customElementResponse.images, ...editTemlate.images];
      template.dropDownLists = [...this.designService.customElementResponse.images, ...editTemlate.dropDownLists]
      this.designService.customTemplateDefinition = template;
      this.reportComponentService.updateCustomTemplate(this.reportCustomElemet.designElementId, JSON.stringify(template), this.customElementName, this.application).subscribe(
        () => { },
        (err) => {
          console.error(err);
          this.spinnerService.hide(this.spinnerService.mainSpinnerId);
          this.snackBar.open(this.translate.instant('customElement.saveFailed'));
        },
        () => {
          this.designService.customElementAdd = false;
          this.designService.isClickedOnCEUpdate = false;
          this.designService.loadReportTemplate(this.designService.templateBeforeEditOfCE, true);
          this.spinnerService.hide(this.spinnerService.mainSpinnerId);
          this.snackBar.open(this.translate.instant('customElement.saveSuccessful'));
        }
      );
    }
    else {
      const template = this.getElementsOnAdd();
      this.elements.forEach((ele) => {
        template[ele].map((obj) => {
          obj.customY = obj.y - parseInt(this.reportCustomElemet.top);
          return obj;
        })
      })
      this.designService.customTemplateDefinition = template;
      this.designService.saveCustomTemplate(this.customElementName, this.application).subscribe(
        () => { },
        (err) => {
          console.error(err);
          this.spinnerService.hide(this.spinnerService.mainSpinnerId);
          this.snackBar.open(this.translate.instant('customElement.saveFailed'));
        },
        () => {
          this.designService.customElementAdd = false;
          this.designService.isClickedOnCEUpdate = false;
          this.designService.loadReportTemplate(this.designService.customDrawTemplate, true);
          this.spinnerService.hide(this.spinnerService.mainSpinnerId);
          this.snackBar.open(this.translate.instant('customElement.saveSuccessful'));
        }
      );
    }

  }

  onCustomElementEdit() {
    this.spinnerService.show(this.spinnerService.mainSpinnerId);
    this.designService.isClickedOnCEUpdate = true;
    this.designService.templateBeforeEditOfCE = { ...this.designService.originTemplate };
    this.designService.templateBeforeEditOfCE.reportTemplateContent = JSON.stringify(
      this.designService.getTemplate()
    );
    const requests = [
      this.reportComponentService.getReportCompopent(this.reportCustomElemet.designElementName),
      this.reportComponentService.getTemplateComponent(this.reportCustomElemet.designElementName)
    ];
    forkJoin(requests).subscribe((res) => {
      this.designService.isClickedOnCEUpdate = true;
      this.application = this.designService.currentCEApplication = res[0].application;
      this.customElementName = this.designService.currentCEName = res[0].name;
      const pageIndex = this.designService.getPageIds().findIndex(x => x == this.pageKey);
      const elementEdit = { ...this.designService.originTemplate };
      elementEdit.reportTemplateContent = JSON.stringify(
        this.designService.getTemplate()
      );
      this.designService.customElementResponse = res[1];
      this.elements.forEach((ele) => {
        (res[1][ele] || []).map((obj) => {
          obj.isCustomElementDraw = true;
          obj.y = obj.customY + parseInt(this.reportCustomElemet.top)
          return obj;
        })
      })
      const temp = JSON.parse(elementEdit.reportTemplateContent);
      temp.bodies[pageIndex].texts = [...temp.bodies[pageIndex].texts, ...res[1].texts];
      temp.bodies[pageIndex].tables = [...temp.bodies[pageIndex].tables, ...res[1].tables];
      temp.bodies[pageIndex].sections = [...temp.bodies[pageIndex].sections, ...res[1].sections];
      temp.bodies[pageIndex].rectangles = [...temp.bodies[pageIndex].rectangles, ...res[1].rectangles];
      temp.bodies[pageIndex].lines = [...temp.bodies[pageIndex].lines, ...res[1].lines];
      temp.bodies[pageIndex].images = [...temp.bodies[pageIndex].images, ...res[1].images];
      temp.bodies[pageIndex].dropDownLists = [...temp.bodies[pageIndex].dropDownLists, ...(res[1]?.dropDownLists || [])];
      elementEdit.reportTemplateContent = JSON.stringify(temp);
      elementEdit.template = temp;
      this.designService.customTemDefinitionBeforeEdit = JSON.parse(JSON.stringify(temp));
      this.designService.loadReportTemplate(elementEdit, true);
    },
      (err) => {
        console.error(err);
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      },
      () => {
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      })
  }

  public onSelectObjectMenu(element, action) {
    this.designService.drawElement.next({ element, action });
  }

  public deleteElement() {
    this.designService.deleteCEItem.next('delete');
  }

  public getElementsOnAdd() {
    const template = new TemplateCustomPageSection();
    const pageIndex = this.designService.getPageIds().findIndex(x => x == this.pageKey);
    if (this.designService.customDrawTemplate) {
      template.texts = this.designService.getTemplate().bodies[pageIndex].texts.filter(x => !JSON.parse(this.designService.customDrawTemplate?.reportTemplateContent).bodies[0].texts.some(y => y.id === x.id));
      template.tables = this.designService.getTemplate().bodies[pageIndex].tables.filter(x => !JSON.parse(this.designService.customDrawTemplate?.reportTemplateContent).bodies[0].tables.some(y => y.id === x.id));
      template.sections = this.designService.getTemplate().bodies[pageIndex].sections.filter(x => !JSON.parse(this.designService.customDrawTemplate?.reportTemplateContent).bodies[0].sections.some(y => y.id === x.id));
      template.rectangles = this.designService.getTemplate().bodies[pageIndex].rectangles.filter(x => !JSON.parse(this.designService.customDrawTemplate?.reportTemplateContent).bodies[0].rectangles.some(y => y.id === x.id));
      template.lines = this.designService.getTemplate().bodies[pageIndex].lines.filter(x => !JSON.parse(this.designService.customDrawTemplate?.reportTemplateContent).bodies[0].lines.some(y => y.id === x.id));
      template.images = this.designService.getTemplate().bodies[pageIndex].images.filter(x => !JSON.parse(this.designService.customDrawTemplate?.reportTemplateContent).bodies[0].images.some(y => y.id === x.id));
      template.dropDownLists = this.designService.getTemplate().bodies[pageIndex].dropDownLists.filter(x => !JSON.parse(this.designService.customDrawTemplate?.reportTemplateContent).bodies[0].dropDownLists.some(y => y.id === x.id));
    }
    return template;
  }

  public getElementsOnUpdate() {
    const template = new TemplateCustomPageSection();
    const pageIndex = this.designService.getPageIds().findIndex(x => x == this.pageKey);
    if (this.designService.customTemDefinitionBeforeEdit) {
      template.texts = this.designService.getTemplate().bodies[pageIndex].texts.filter(x => !this.designService.customTemDefinitionBeforeEdit.bodies[pageIndex].texts.some(y => y.id === x.id));
      template.tables = this.designService.getTemplate().bodies[pageIndex].tables.filter(x => !this.designService.customTemDefinitionBeforeEdit.bodies[pageIndex].tables.some(y => y.id === x.id));
      template.sections = this.designService.getTemplate().bodies[pageIndex].sections.filter(x => !this.designService.customTemDefinitionBeforeEdit.bodies[pageIndex].sections.some(y => y.id === x.id));
      template.rectangles = this.designService.getTemplate().bodies[pageIndex].rectangles.filter(x => !this.designService.customTemDefinitionBeforeEdit.bodies[pageIndex].rectangles.some(y => y.id === x.id));
      template.lines = this.designService.getTemplate().bodies[pageIndex].lines.filter(x => !this.designService.customTemDefinitionBeforeEdit.bodies[pageIndex].lines.some(y => y.id === x.id));
      template.images = this.designService.getTemplate().bodies[pageIndex].images.filter(x => !this.designService.customTemDefinitionBeforeEdit.bodies[pageIndex].images.some(y => y.id === x.id));
      template.dropDownLists = this.designService.getTemplate().bodies[pageIndex].dropDownLists.filter(x => !this.designService.customTemDefinitionBeforeEdit.bodies[pageIndex].dropDownLists.some(y => y.id === x.id));
    }
    return template;
  }

  public getLastElementYPosition(): string {
    const templateForAdd = this.getElementsOnAdd();
    return [Math.max(0, ...[
      ...(templateForAdd.texts || []).map(x => (x.y - parseInt(this.reportCustomElemet.top) + x.height)),
      ...(templateForAdd.images || []).map(x => (x.y - parseInt(this.reportCustomElemet.top) + x.height)),
      ...(templateForAdd.lines || []).map(x => (x.y - parseInt(this.reportCustomElemet.top) + x.height)),
      ...(templateForAdd.rectangles || []).map(x => (x.y - parseInt(this.reportCustomElemet.top) + x.height)),
      ...(templateForAdd.sections || []).map(x => (x.y - parseInt(this.reportCustomElemet.top) + x.height)),
      ...(templateForAdd.tables || []).map(x => (x.y - parseInt(this.reportCustomElemet.top) + x.height)),
      ...(templateForAdd.dropDownLists || []).map(x => (x.y - parseInt(this.reportCustomElemet.top) + x.height))].filter(Number.isFinite)
    ) + 2, 'mm'].join('');
  }

  public onResizeMarkerDown(event: MouseEvent, vertical: string, horizontal: string) {
    event.preventDefault();
    this.resizing = true;
    const beginPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };
    this.designService.notifyBeginResize(this.notifier, ElementType.Text, horizontal, vertical, beginPoint);

    event.stopPropagation();
  }

  public onResizing(event: MouseEvent) {
    event.preventDefault();
    if (this.resizing) {
      this.designService.notifyResize(this.notifier, { onElementX: event.offsetX, onElementY: event.offsetY });
    }
    event.stopPropagation();
  }

  public onResizeMarkerUp(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }

  public onResizeMarkerLeave(event: MouseEvent) {
    event.preventDefault();
    this.resizing = false;
    event.stopPropagation();
  }
}
